import { useMemo, useState } from 'react';
import { Plus, Loader2, Briefcase, Pencil, Trash2, PackagePlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { formatMoney } from '@/lib/money';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useAuth } from '@/hooks/useAuth';
import {
  useRetailVendors,
  useCreateRetailVendor,
  useUpdateRetailVendor,
  useDeleteRetailVendor,
  useReceiveStock,
} from '@/hooks/api/useRetailVendors';
import { useRetailProducts } from '@/hooks/api/useRetailProducts';
import { format } from 'date-fns';
import { UserRole, type Vendor, type RetailProduct } from '@pms/shared';

const ADMIN_ROLES = new Set<UserRole>([UserRole.ADMIN, UserRole.SUPER_ADMIN]);
const RECEIVE_ROLES = new Set<UserRole>([
  UserRole.OPERATIONS,
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN,
]);

// ─── Page ────────────────────────────────────────────────────────────────────

export function VendorsPage() {
  const propertyId = useSelectedPropertyId();
  const { user } = useAuth();
  const canEditVendors = !!user && ADMIN_ROLES.has(user.role);
  const canReceive = !!user && RECEIVE_ROLES.has(user.role);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [showReceive, setShowReceive] = useState(false);

  const listQuery = useRetailVendors({ propertyId }, !!propertyId);
  const deleteMutation = useDeleteRetailVendor();
  const vendors: Vendor[] = Array.isArray(listQuery.data)
    ? listQuery.data
    : ((listQuery.data as { items?: Vendor[] } | undefined)?.items ?? []);

  function handleDelete(v: Vendor) {
    if (!window.confirm('Delete this vendor?')) return;
    deleteMutation.mutate(v.id, {
      onError: (err) => {
        // Server soft-blocks when referenced by receive history (409 IN_USE).
        window.alert(extractErrorMessage(err));
      },
    });
  }

  return (
    <div>
      <PageHeader
        title="Vendors"
        description="Supplier directory and stock receiving"
        actions={
          <div className="flex items-center gap-3">
            {canReceive && (
              <Button variant="secondary" onClick={() => setShowReceive(true)} disabled={!propertyId}>
                <PackagePlus size={16} aria-hidden="true" />
                Receive Stock
              </Button>
            )}
            {canEditVendors && (
              <Button onClick={() => { setEditing(null); setShowForm(true); }} disabled={!propertyId}>
                <Plus size={16} aria-hidden="true" />
                Add Vendor
              </Button>
            )}
          </div>
        }
      />

      {!propertyId ? (
        <EmptyState icon={Briefcase} title="Select a property" description="Select a property to continue." />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : listQuery.isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : vendors.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          title="No vendors"
          description="Add a vendor to track suppliers and receive stock."
          {...(canEditVendors
            ? { actionLabel: 'Add Vendor', onAction: () => { setEditing(null); setShowForm(true); } }
            : {})}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Contact</Th>
                  <Th>Terms</Th>
                  <Th>Active</Th>
                  {canEditVendors && <Th>Actions</Th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {vendors.map((v) => (
                  <tr key={v.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{v.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{v.contactName ?? v.email ?? v.phone ?? '--'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{v.terms ?? '--'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                          v.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
                        )}
                      >
                        {v.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {canEditVendors && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => { setEditing(v); setShowForm(true); }} className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50" aria-label="Edit">
                            <Pencil size={14} />
                          </button>
                          <button type="button" onClick={() => handleDelete(v)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Delete">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        title={editing ? 'Edit Vendor' : 'Add Vendor'}
        width="max-w-xl"
      >
        {propertyId && (
          <VendorForm
            propertyId={propertyId}
            editing={editing}
            onClose={() => { setShowForm(false); setEditing(null); }}
          />
        )}
      </SlideOver>

      <SlideOver
        isOpen={showReceive}
        onClose={() => setShowReceive(false)}
        title="Receive Stock"
        width="max-w-xl"
      >
        {propertyId && (
          <ReceiveStockForm propertyId={propertyId} onClose={() => setShowReceive(false)} />
        )}
      </SlideOver>
    </div>
  );
}

// ─── Vendor form ─────────────────────────────────────────────────────────────

function VendorForm({
  propertyId,
  editing,
  onClose,
}: {
  propertyId: string;
  editing: Vendor | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [contactName, setContactName] = useState(editing?.contactName ?? '');
  const [email, setEmail] = useState(editing?.email ?? '');
  const [phone, setPhone] = useState(editing?.phone ?? '');
  const [terms, setTerms] = useState(editing?.terms ?? '');
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);

  const createMutation = useCreateRetailVendor();
  const updateMutation = useUpdateRetailVendor();
  const mutationError = createMutation.error ?? updateMutation.error;
  const isPending = createMutation.isPending || updateMutation.isPending;

  const isValid = name.trim().length > 0;

  function clearError() {
    if (createMutation.isError) createMutation.reset();
    if (updateMutation.isError) updateMutation.reset();
  }

  function submit(nextActive: boolean) {
    if (!isValid) return;
    const fields = {
      name: name.trim(),
      contactName: contactName.trim() || undefined,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      terms: terms.trim() || undefined,
      isActive: nextActive,
    };
    if (editing) {
      updateMutation.mutate({ id: editing.id, payload: fields }, { onSuccess: () => onClose() });
    } else {
      createMutation.mutate({ propertyId, ...fields }, { onSuccess: () => onClose() });
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    submit(isActive);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="v-name" className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input id="v-name" type="text" required value={name} onChange={(e) => { setName(e.target.value); clearError(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="v-contact" className="block text-sm font-medium text-gray-700 mb-1">Contact name</label>
        <input id="v-contact" type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="v-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input id="v-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        </div>
        <div>
          <label htmlFor="v-phone" className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <input id="v-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        </div>
      </div>
      <div>
        <label htmlFor="v-terms" className="block text-sm font-medium text-gray-700 mb-1">Terms</label>
        <input id="v-terms" type="text" value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="e.g. Net 30" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div className="flex items-center gap-2">
        <input id="v-active" type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
        <label htmlFor="v-active" className="text-sm font-medium text-gray-700">Active</label>
      </div>

      {mutationError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(mutationError)}
        </p>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div>
          {editing && editing.isActive && (
            <Button type="button" variant="secondary" onClick={() => submit(false)} isLoading={isPending}>
              Deactivate
            </Button>
          )}
        </div>
        <div className="flex gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" isLoading={isPending} disabled={!isValid}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </form>
  );
}

// ─── Receive stock form ──────────────────────────────────────────────────────

function ReceiveStockForm({ propertyId, onClose }: { propertyId: string; onClose: () => void }) {
  const [vendorId, setVendorId] = useState('');
  const [productId, setProductId] = useState('');
  const [qty, setQty] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [reference, setReference] = useState('');
  const [date, setDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [productSearch, setProductSearch] = useState('');

  const vendorsQuery = useRetailVendors({ propertyId, activeOnly: true }, !!propertyId);
  const productsQuery = useRetailProducts({ propertyId, isActive: true }, !!propertyId);
  const receiveMutation = useReceiveStock();

  const activeVendors: Vendor[] = vendorsQuery.data ?? [];
  const allProducts: RetailProduct[] = productsQuery.data?.data ?? [];
  const products: RetailProduct[] = useMemo(() => {
    if (!productSearch.trim()) return allProducts;
    const q = productSearch.toLowerCase();
    return allProducts.filter(
      (p) => p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q),
    );
  }, [allProducts, productSearch]);

  const qtyNum = parseInt(qty, 10);
  const qtyValid = Number.isInteger(qtyNum) && qtyNum >= 1;
  const costNum = parseFloat(unitCost);
  const costValid = Number.isFinite(costNum) && costNum >= 0;
  const totalCost = qtyValid && costValid ? qtyNum * costNum : 0;
  const isValid = !!vendorId && !!productId && qtyValid && costValid;

  function clearError() {
    if (receiveMutation.isError) receiveMutation.reset();
  }

  function normalizeCost() {
    if (!unitCost.trim()) return;
    const n = parseFloat(unitCost);
    if (Number.isFinite(n) && n >= 0) setUnitCost(n.toFixed(2));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    receiveMutation.mutate(
      {
        propertyId,
        productId,
        vendorId,
        qty: qtyNum,
        unitCost: costNum,
        reference: reference.trim() || undefined,
        date,
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="rs-vendor" className="block text-sm font-medium text-gray-700 mb-1">
          Vendor <span className="text-red-500">*</span>
        </label>
        <select id="rs-vendor" value={vendorId} onChange={(e) => { setVendorId(e.target.value); clearError(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="">Select a vendor…</option>
          {activeVendors.map((v) => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="rs-product-search" className="block text-sm font-medium text-gray-700 mb-1">
          Product <span className="text-red-500">*</span>
        </label>
        <input
          id="rs-product-search"
          type="search"
          value={productSearch}
          onChange={(e) => setProductSearch(e.target.value)}
          placeholder="Search by name or SKU…"
          className="mb-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <select
          id="rs-product"
          value={productId}
          onChange={(e) => { setProductId(e.target.value); clearError(); }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          size={Math.min(6, Math.max(2, products.length))}
        >
          {products.length === 0 ? (
            <option value="" disabled>No active products</option>
          ) : (
            products.map((p) => (
              <option key={p.id} value={p.id}>{p.name} — {p.sku}</option>
            ))
          )}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="rs-qty" className="block text-sm font-medium text-gray-700 mb-1">
            Quantity <span className="text-red-500">*</span>
          </label>
          <input
            id="rs-qty"
            type="number"
            step="1"
            min="1"
            required
            value={qty}
            onChange={(e) => { setQty(e.target.value); clearError(); }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor="rs-cost" className="block text-sm font-medium text-gray-700 mb-1">
            Unit cost <span className="text-red-500">*</span>
          </label>
          <input
            id="rs-cost"
            type="number"
            step="0.01"
            min="0"
            required
            value={unitCost}
            onChange={(e) => { setUnitCost(e.target.value); clearError(); }}
            onBlur={normalizeCost}
            placeholder="0.00"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="rs-ref" className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
          <input id="rs-ref" type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        </div>
        <div>
          <label htmlFor="rs-date" className="block text-sm font-medium text-gray-700 mb-1">Date</label>
          <input id="rs-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        </div>
      </div>

      <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-700" aria-live="polite">
        Total cost: <span className="font-semibold">{formatMoney(totalCost)}</span>
      </div>

      {receiveMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(receiveMutation.error)}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={receiveMutation.isPending} disabled={!isValid}>Receive</Button>
      </div>
    </form>
  );
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
